import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Close, MarkEmailReadOutlined } from "@mui/icons-material";
import { useAuth } from "../authContext/authContext";
import { updateMyProfile } from "../Services/apiService";
import { consumeTwoFactorPrompt } from "../Services/sessionStorage";
import { BRAND_BLUE } from "../theme/appTheme";
import { useTranslation } from "../i18n/LanguageContext";

const buttonBaseSx = {
  textTransform: "none",
  borderRadius: 1.5,
  py: 1,
  fontSize: "0.8125rem",
  fontWeight: 600,
  boxShadow: "none",
};

// Shown right after a fresh login while the user hasn't chosen whether they
// want the emailed login code (users.two_factor_enabled IS NULL). Closing it
// without picking saves nothing, so it comes back on their next login.
export default function TwoFactorChoiceDialog() {
  const { t } = useTranslation();
  const { user, updateUserFields } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null); // true | false while saving that choice
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    if (consumeTwoFactorPrompt() && user.twoFactorEnabled === null) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const choose = async (value) => {
    setSaving(value);
    setError("");
    try {
      await updateMyProfile({ twoFactorEnabled: value });
      updateUserFields({ twoFactorEnabled: value });
      setOpen(false);
    } catch {
      setError(t("twoFactorPrompt.error"));
    } finally {
      setSaving(null);
    }
  };

  const busy = saving !== null;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && setOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        disabled={busy}
        size="small"
        sx={{ position: "absolute", top: 10, right: 10, color: "#9ca3af" }}
      >
        <Close fontSize="small" />
      </IconButton>

      <DialogContent sx={{ px: { xs: 3, sm: 4 }, pt: 4, pb: 3 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "#EEF2F8",
            color: BRAND_BLUE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <MarkEmailReadOutlined fontSize="small" />
        </Box>

        <Typography sx={{ fontSize: "1.0625rem", fontWeight: 600, color: "#111827", mb: 1 }}>
          {t("twoFactorPrompt.title")}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#4b5563", lineHeight: 1.55, mb: 3 }}>
          {t("twoFactorPrompt.body")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Button
            variant="contained"
            onClick={() => choose(true)}
            disabled={busy}
            sx={{ ...buttonBaseSx, bgcolor: BRAND_BLUE, color: "#fff", "&:hover": { bgcolor: BRAND_BLUE, opacity: 0.92, boxShadow: "none" } }}
          >
            {saving === true ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : t("twoFactorPrompt.enable")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => choose(false)}
            disabled={busy}
            sx={{ ...buttonBaseSx, color: "#374151", borderColor: "#d0d5dd", "&:hover": { borderColor: "#c5c5c5", bgcolor: "#F7F7F7" } }}
          >
            {saving === false ? <CircularProgress size={16} sx={{ color: "#374151" }} /> : t("twoFactorPrompt.disable")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ fontSize: "0.8125rem", mt: 2 }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", mt: 2.5, textAlign: "center" }}>
          {t("twoFactorPrompt.changeLater")}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
